import {
  type CancelSubscriptionResponseDto,
  SeatsApi,
  type SubscribeDtoPlanNameEnum,
  type SubscribeResponseDto,
  type SubscriptionPlanDto,
  SubscriptionsApi,
} from '@saaga/billingv2-sdk'
import { useCallback } from 'react'

import { parseBillingError } from '@/utils/billing-error.util'

import { useBillingConfig } from './use-billing-config.hook'

const buildReturnUrl = (): string =>
  `${window.location.origin}${window.location.pathname}?checkout_completed=1&session_id={CHECKOUT_SESSION_ID}`

export interface UseSubscriptionApiReturn {
  getSubscriptionsClient: () => Promise<SubscriptionsApi>
  getSeatsClient: () => Promise<SeatsApi>
  getPlans: () => Promise<SubscriptionPlanDto[]>
  subscribe: (planName: string, isYearly: boolean) => Promise<SubscribeResponseDto>
  cancelSubscription: () => Promise<CancelSubscriptionResponseDto>
}

export const useSubscriptionApi = (): UseSubscriptionApiReturn => {
  const { createConfig } = useBillingConfig()

  const getSubscriptionsClient = useCallback(async () => {
    const config = await createConfig()
    return new SubscriptionsApi(config)
  }, [createConfig])

  const getSeatsClient = useCallback(async () => {
    const config = await createConfig()
    return new SeatsApi(config)
  }, [createConfig])

  const getPlans = useCallback(async (): Promise<SubscriptionPlanDto[]> => {
    try {
      const client = await getSubscriptionsClient()
      return await client.subscriptionsControllerGetPlans()
    } catch (err) {
      throw new Error(await parseBillingError(err))
    }
  }, [getSubscriptionsClient])

  const subscribe = useCallback(
    async (planName: string, isYearly: boolean): Promise<SubscribeResponseDto> => {
      try {
        const client = await getSubscriptionsClient()
        return await client.subscriptionsControllerSubscribe({
          subscribeDto: {
            planName: planName as SubscribeDtoPlanNameEnum,
            isYearly,
            returnUrl: buildReturnUrl(),
          },
        })
      } catch (err) {
        throw new Error(await parseBillingError(err))
      }
    },
    [getSubscriptionsClient]
  )

  const cancelSubscription = useCallback(async (): Promise<CancelSubscriptionResponseDto> => {
    try {
      const client = await getSubscriptionsClient()
      return await client.subscriptionsControllerCancelSubscription()
    } catch (err) {
      throw new Error(await parseBillingError(err))
    }
  }, [getSubscriptionsClient])

  return { getSubscriptionsClient, getSeatsClient, getPlans, subscribe, cancelSubscription }
}
